import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Eye, EyeOff, Upload, GripVertical, Search } from 'lucide-react';

interface WofItem {
  id: string;
  name: string;
  team: string | null;
  image_url: string | null;
  sort_order: number;
  visible: boolean;
}

const WallOfFameAdmin = () => {
  const [items, setItems] = useState<WofItem[]>([]);
  const [newName, setNewName] = useState('');
  const [newTeam, setNewTeam] = useState('');
  const [search, setSearch] = useState('');
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const fetchItems = async () => {
    const { data } = await supabase.from('wall_of_fame').select('*').order('sort_order');
    if (data) setItems(data as WofItem[]);
  };

  useEffect(() => { fetchItems(); }, []);

  const addItem = async () => {
    if (!newName.trim()) return;
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;
    const { error } = await supabase.from('wall_of_fame').insert({
      name: newName.trim(),
      team: newTeam.trim() || null,
      sort_order: maxOrder,
    });
    if (error) return toast.error(error.message);
    toast.success('Přidáno!');
    setNewName('');
    setNewTeam('');
    fetchItems();
  };

  const updateItem = async (id: string, updates: Partial<WofItem>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
    await supabase.from('wall_of_fame').update(updates).eq('id', id);
  };

  const deleteItem = async (id: string) => {
    if (!confirm('Opravdu smazat?')) return;
    await supabase.from('wall_of_fame').delete().eq('id', id);
    toast.success('Smazáno');
    fetchItems();
  };

  const uploadImage = async (id: string, file: File) => {
    const rawExt = file.name.split('.').pop() || 'jpg';
    const ext = rawExt.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const path = `${id}.${ext}`;
    const { error } = await supabase.storage.from('wof-images').upload(path, file, { upsert: true });
    if (error) return toast.error(error.message);
    const { data: urlData } = supabase.storage.from('wof-images').getPublicUrl(path);
    await updateItem(id, { image_url: urlData.publicUrl });
    toast.success('Obrázek nahrán');
  };

  const handleDragStart = (index: number) => { dragIndex.current = index; };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOver(index);
  };
  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOver(null);
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === dropIndex) return;

    const updated = [...items];
    const [moved] = updated.splice(from, 1);
    updated.splice(dropIndex, 0, moved);
    setItems(updated);

    const promises = updated.map((item, i) =>
      supabase.from('wall_of_fame').update({ sort_order: i }).eq('id', item.id)
    );
    await Promise.all(promises);
    toast.success('Pořadí uloženo');
  };

  const filtered = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    (i.team || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <h1 className="text-4xl tracking-wider text-foreground mb-8" style={{ fontFamily: 'Bebas Neue' }}>
        Wall of Fame ({items.length})
      </h1>

      <div className="flex flex-wrap gap-3 mb-6">
        <Input placeholder="Jméno" value={newName} onChange={(e) => setNewName(e.target.value)} className="bg-card border-border max-w-[200px]" onKeyDown={(e) => e.key === 'Enter' && addItem()} />
        <Input placeholder="Tým" value={newTeam} onChange={(e) => setNewTeam(e.target.value)} className="bg-card border-border max-w-[200px]" onKeyDown={(e) => e.key === 'Enter' && addItem()} />
        <Button onClick={addItem}><Plus size={16} /> Přidat</Button>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Hledat podle jména nebo týmu..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-background border-border"
        />
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        💡 Tip: Tažením karet můžete měnit pořadí (funguje pouze bez vyhledávání).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((item) => {
          const realIndex = items.indexOf(item);
          return (
            <div
              key={item.id}
              draggable={!search}
              onDragStart={() => handleDragStart(realIndex)}
              onDragOver={(e) => handleDragOver(e, realIndex)}
              onDrop={(e) => handleDrop(e, realIndex)}
              onDragEnd={() => setDragOver(null)}
              className={`p-4 bg-card border rounded-md space-y-3 transition-all ${
                dragOver === realIndex ? 'border-primary scale-[1.02]' : 'border-border'
              } ${!search ? 'cursor-move' : ''}`}
            >
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <GripVertical size={14} />
                <span>#{realIndex + 1}</span>
              </div>

              <div className="aspect-[4/5] rounded overflow-hidden bg-muted/30 relative group">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">Bez fotky</div>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Upload size={24} className="text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(item.id, file);
                  }} />
                </label>
              </div>

              <Input value={item.name} onChange={(e) => updateItem(item.id, { name: e.target.value })} className="bg-background border-border text-sm" placeholder="Jméno" />
              <Input value={item.team || ''} onChange={(e) => updateItem(item.id, { team: e.target.value })} className="bg-background border-border text-sm" placeholder="Tým" />

              <div className="flex justify-end gap-2">
                <button onClick={() => updateItem(item.id, { visible: !item.visible })} className={`p-1.5 ${item.visible ? 'text-primary' : 'text-muted-foreground'}`}>
                  {item.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => deleteItem(item.id)} className="p-1.5 text-muted-foreground hover:text-destructive">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && items.length > 0 && (
          <p className="col-span-full text-center text-muted-foreground py-12">Žádné výsledky pro "{search}".</p>
        )}
      </div>
    </div>
  );
};

export default WallOfFameAdmin;
